import { pick } from 'lodash';

import { db, FieldValue } from '@/service/firebase';

import store from '@/store';

let activeImportJobsUnsub: null | (() => void) = null;

export const COL = 'catalogItemImportJobs';
export const CATALOG_ITEM_IMPORT_PERMISSION = 'catalog-item:import';
export const PAYLOAD_BYTES_WARN = 900000;
export const ACTIVE = ['queued', 'processing'] as const;
export type ImportJobStatus = (typeof ACTIVE)[number] | 'completed' | 'failed';

type CreatedBy = ReturnType<typeof pickCreatedBy>;

function pickCreatedBy() {
  const u = store.getters['auth/user']?.employee;
  if (!u) return null;
  return pick(u, ['firstName', 'lastName', 'id', 'workflowFunction', 'code']);
}

const ELEVATED_IMPORT_JOB_VIEWER_WORKFLOWS = new Set(['administrator', 'global administrator', 'developer']);

export function canViewAllCatalogImportJobs(): boolean {
  const e = store.getters['auth/user']?.employee as Record<string, unknown> | null | undefined;
  if (!e) return false;
  const r = e.role as { name?: string } | undefined;
  if (r?.name) {
    const rl = String(r.name).toLowerCase();
    if (rl === 'admin' || rl === 'super admin' || rl === 'developer') {
      return true;
    }
  }
  const wf = e.workflowFunction as { name?: string } | undefined;
  if (wf?.name) {
    if (ELEVATED_IMPORT_JOB_VIEWER_WORKFLOWS.has(String(wf.name).toLowerCase())) {
      return true;
    }
  }
  return false;
}

export function payloadSizeHint(rows: unknown[]) {
  try {
    return new TextEncoder().encode(JSON.stringify(rows)).length;
  } catch {
    return 0;
  }
}

export type CatalogItemImportJobDoc = {
  status: ImportJobStatus | string;
  catalogItemType: string;
  rows?: unknown[];
  createdBy?: CreatedBy;
  createdAt?: { seconds?: number; nanoseconds?: number };
  progress?: { done: number; total: number };
  finishedErrors?: { index: number; message: string }[];
  createdSuppliers?: { index: number; id: string; name?: string }[];
  importedCatalogItems?: { index: number; catalogItemId: string; name?: string }[];
  createdDetailListValues?: { index: number; field: string; value: string }[];
};

export function subscribeJob(
  id: string,
  onNext: (doc: { id: string } & Record<string, unknown>) => void,
  onError: (e: Error) => void
) {
  return db
    .collection(COL)
    .doc(id)
    .onSnapshot(
      s => {
        if (!s.exists) {
          onError(new Error('Job not found'));
          return;
        }
        onNext({ id: s.id, ...s.data() } as { id: string } & Record<string, unknown>);
      },
      e => onError(e instanceof Error ? e : new Error(String(e)))
    );
}

export function startActiveImportJobsListener() {
  if (activeImportJobsUnsub) return;
  stopActiveImportJobsListener();
  const q = db.collection(COL).where('status', 'in', [...ACTIVE]);
  activeImportJobsUnsub = q.onSnapshot(
    snap => {
      const jobs = snap.docs.map(d => ({ id: d.id, ...(d.data() as Record<string, unknown>) }));
      store.commit('catalogItemImport/setActiveImportJobs', jobs);
    },
    e => {
      console.error('Active catalog import jobs listener', e);
    }
  );
}

export function stopActiveImportJobsListener() {
  if (activeImportJobsUnsub) {
    activeImportJobsUnsub();
    activeImportJobsUnsub = null;
  }
  store.commit('catalogItemImport/setActiveImportJobs', []);
}

export const IMPORT_JOB_LIST_LIMIT = 300;

export type ImportJobListRow = {
  id: string;
  status?: string;
  catalogItemType?: string;
  createdBy?: Record<string, unknown>;
  createdAt?: unknown;
  progress?: { done: number; total: number };
  error?: string;
  finishedErrors?: { index: number; message: string }[];
  createdSuppliers?: { index: number; id: string; name?: string }[];
  importedCatalogItems?: { index: number; catalogItemId: string; name?: string }[];
  createdDetailListValues?: { index: number; field: string; value: string }[];
};

function mapDocToImportJobListRow(d: { id: string; data: () => Record<string, unknown> }): ImportJobListRow {
  const x = d.data() as Record<string, unknown>;
  return {
    id: d.id,
    status: x.status != null ? String(x.status) : undefined,
    catalogItemType: x.catalogItemType != null ? String(x.catalogItemType) : undefined,
    createdBy: (x.createdBy as Record<string, unknown> | undefined) ?? undefined,
    createdAt: x.createdAt,
    progress: x.progress as ImportJobListRow['progress'] | undefined,
    error: x.error != null ? String(x.error) : undefined,
    finishedErrors: Array.isArray(x.finishedErrors)
      ? (x.finishedErrors as ImportJobListRow['finishedErrors'])
      : undefined,
    createdSuppliers: Array.isArray(x.createdSuppliers)
      ? (x.createdSuppliers as ImportJobListRow['createdSuppliers'])
      : undefined,
    importedCatalogItems: Array.isArray(x.importedCatalogItems)
      ? (x.importedCatalogItems as ImportJobListRow['importedCatalogItems'])
      : undefined,
    createdDetailListValues: Array.isArray(x.createdDetailListValues)
      ? (x.createdDetailListValues as ImportJobListRow['createdDetailListValues'])
      : undefined,
  };
}

function importJobListQuery() {
  const uid = store.getters['auth/user']?.employee?.id;
  if (uid == null) {
    return null;
  }
  const all = canViewAllCatalogImportJobs();
  const col = db.collection(COL);
  if (all) {
    return col.orderBy('createdAt', 'desc').limit(IMPORT_JOB_LIST_LIMIT);
  }
  return col.where('createdBy.id', '==', uid).orderBy('createdAt', 'desc').limit(IMPORT_JOB_LIST_LIMIT);
}

export async function fetchMyImportJobsForList(): Promise<ImportJobListRow[]> {
  const q = importJobListQuery();
  if (q == null) {
    return [];
  }
  const snap = await q.get();
  return snap.docs.map(d => mapDocToImportJobListRow(d as { id: string; data: () => Record<string, unknown> }));
}

export function subscribeMyImportJobsForList(
  onNext: (rows: ImportJobListRow[]) => void,
  onError?: (e: Error) => void
): () => void {
  const q = importJobListQuery();
  if (q == null) {
    onNext([]);
    return () => {
      return undefined;
    };
  }
  return q.onSnapshot(
    snap => {
      onNext(snap.docs.map(d => mapDocToImportJobListRow(d as { id: string; data: () => Record<string, unknown> })));
    },
    e => {
      if (onError) {
        onError(e instanceof Error ? e : new Error(String(e)));
      }
    }
  );
}

export async function queueImportJob(catalogItemType: string, rows: unknown[]) {
  if (!store.getters['auth/hasPermission'](CATALOG_ITEM_IMPORT_PERMISSION)) {
    throw new Error('You do not have permission to import catalog items');
  }
  const createdBy = pickCreatedBy();
  if (!createdBy) {
    throw new Error('Not signed in');
  }
  const n = payloadSizeHint(rows);
  if (n > PAYLOAD_BYTES_WARN) {
    throw new Error('Import payload is too large for a single job; split the file or reduce row count');
  }
  const t = String(catalogItemType).trim().toLowerCase();
  const jobRef = db.collection(COL).doc();
  const now = FieldValue.serverTimestamp();

  await jobRef.set({
    status: 'queued',
    catalogItemType: t,
    rows,
    createdAt: now,
    createdBy,
    progress: { done: 0, total: rows.length },
  });

  return { id: jobRef.id };
}

export { pickCreatedBy };
